import { ThemeOptions } from '@mui/material';

import { cssVars } from '../../utils/cssVars';
import { tokens } from '../../theme/generated/tokens';

const listSubheaderVars = cssVars(
  {
    minHeight: undefined,
    padding: undefined,
  },
  'ds-list'
);

const listItemVars = cssVars(
  {
    minHeight: undefined,
    padding: undefined,
    adornmentGap: undefined,
    avatarGap: undefined,
    radioGap: undefined,
    color: undefined,
    colorSelected: undefined,
    colorDisabled: undefined,
    background: undefined,
    backgroundHover: undefined,
  },
  'ds-list-item'
);

const listSearchInput = cssVars(
  {
    adornmentGap: undefined,
    inputGap: undefined,
  },
  'ds-list-search'
);

export const listTheme: ThemeOptions['components'] = {
  DsList: {
    styleOverrides: {
      root: {
        cursor: 'text',
        padding: 0,
        '& .DsListItem-root': {
          margin: '1px 0',
        },
        '&:not(.Ds-List-Nested) .DsListItem-root:first-of-type': {
          marginTop: '0',
        },
        '& .DsListItem-root:last-of-type': {
          marginBottom: '0',
        },
      },
    },
  },
  DsListSubheader: {
    styleOverrides: {
      root: {
        padding: listSubheaderVars.getters.padding(),
        minHeight: listSubheaderVars.getters.minHeight(),
        color: tokens.theme.fg.structural.secondary,
        backgroundColor: tokens.theme.bg.structural.surface,
      },
      large: ({ disableGutters }) =>
        listSubheaderVars.setValues({
          minHeight: '40px',
          padding: disableGutters ? '0px' : '17px 12px 11px',
        }),
      medium: ({ disableGutters }) =>
        listSubheaderVars.setValues({
          minHeight: '32px',
          padding: disableGutters ? '0px' : '13px 10px 7px',
        }),
      small: ({ disableGutters }) =>
        listSubheaderVars.setValues({
          minHeight: '28px',
          padding: disableGutters ? '0px' : '11px 8px 5px',
        }),
    },
  },
  DsListItem: {
    styleOverrides: {
      root: () => ({
        ...listItemVars.setValues({
          color: tokens.theme.fg.structural.primary,
          background: tokens.theme.bg.structural.surface,
          backgroundHover: tokens.theme.bg.action.surfaceHover,
        }),

        minHeight: listItemVars.getters.minHeight(),
        padding: listItemVars.getters.padding(),

        '&&': {
          color: listItemVars.refs.color,
          backgroundColor: listItemVars.refs.background,
        },

        '& .DsListItem-content': {
          flex: '1 1 auto',
        },
        '& .MuiListItemIcon-alignItemsFlexStart': {
          marginTop: 0,
        },

        '& .DsListItem-adornment-start': {
          display: 'inherit',
          marginRight: listItemVars.getters.adornmentGap(),
          color: tokens.theme.fg.structural.secondary,

          '&.DsListItem-adornment-avatar': {
            marginRight: listItemVars.getters.avatarGap(),
          },
          '&.DsListItem-adornment-radio': {
            marginRight: listItemVars.getters.radioGap(),
          },
        },
        '& .DsListItem-adornment-end': {
          display: 'inherit',
          marginLeft: listItemVars.getters.adornmentGap(),
          color: tokens.theme.fg.structural.secondary,

          '&.DsListItem-adornment-avatar': {
            marginLeft: listItemVars.getters.avatarGap(),
          },
          '&.DsListItem-adornment-radio': {
            marginLeft: listItemVars.getters.radioGap(),
          },
        },
      }),
      large: ({ disableGutters, ownerState }) =>
        listItemVars.setValues({
          minHeight: '40px',
          padding: disableGutters
            ? '0px'
            : ownerState.multiline
            ? '12px 16px'
            : `0 ${ownerState.nested ? '24px' : '12px'}`,
          adornmentGap: '8px',
          avatarGap: '12px',
          radioGap: '12px',
        }),
      medium: ({ disableGutters, ownerState }) =>
        listItemVars.setValues({
          minHeight: '32px',
          padding: disableGutters
            ? '0px'
            : ownerState.multiline
            ? '10px 12px'
            : `0 ${ownerState.nested ? '22px' : '10px'}`,
          adornmentGap: '8px',
          avatarGap: '12px',
          radioGap: '12px',
        }),
      small: ({ disableGutters, ownerState }) =>
        listItemVars.setValues({
          minHeight: '28px',
          padding: disableGutters
            ? '0px'
            : ownerState.multiline
            ? '5px 12px'
            : `0 ${ownerState.nested ? '20px' : '8px'}`,
          adornmentGap: '4px',
          avatarGap: '8px',
          radioGap: '8px',
        }),
      hover: {
        // Doubled specificity so our hover/focus background wins over MUI's base
        // :hover, .Mui-selected:hover, and default focusVisible backgroundColor rules.
        // Matches DropdownItem "highlighted" (bg/action/surface-hover).
        '&&:hover, &&.Mui-focusVisible': {
          backgroundColor: listItemVars.refs.backgroundHover,
          cursor: 'pointer',
          userSelect: 'none',
        },
      },
      selected: () => ({
        ...listItemVars.setValues({
          background: tokens.theme.bg.selection.primarySubtle,
          backgroundHover: tokens.theme.bg.selection.primarySubtleHover,
          color: tokens.theme.fg.selection.onPriamrySubtle,
        }),
      }),

      disabled: ({ selected }) => ({
        ...listItemVars.setValues({
          color: tokens.theme.disabled.fg,
          background: selected
            ? tokens.theme.disabled.bg
            : tokens.theme.bg.structural.surface,
        }),

        '&.Mui-disabled': {
          opacity: 1,

          '&:hover': {
            cursor: 'not-allowed',
            userSelect: 'text',
          },
        },
      }),
    },
  },
  DsListItemText: {
    styleOverrides: {
      root: {
        margin: 0,

        '.Mui-disabled & .MuiListItemText-secondary': {
          color: 'inherit',
        },
      },
    },
  },
  DsListItemIcon: {
    styleOverrides: {
      root: {
        minWidth: 'unset',
        color: 'inherit',

        '.Mui-selected &': {
          color: 'inherit',
        },
      },
    },
  },
  DsListItemAvatar: {
    styleOverrides: {
      root: {
        minWidth: 'unset',
        margin: 0,
      },
    },
  },
  DsListSearchInput: {
    styleOverrides: {
      root: {
        borderBottom: `1px solid ${tokens.theme.border.structural.neutral}`,

        '& .DS-Input-startOutsideAdornment': {
          paddingLeft: listSearchInput.getters.adornmentGap(),
          color: tokens.theme.fg.structural.secondary,
          '&.DsInput-disabled': {
            color: tokens.theme.disabled.fg,
          },
        },

        '&:focus-within': {
          '& .DS-Input-startOutsideAdornment': {
            color: tokens.theme.fg.selection.primary,
          },
        },

        '& .DS-Input-endOutsideAdornment': {
          paddingRight: listSearchInput.getters.adornmentGap(),

          '&:has(.clearInput)': {
            paddingRight: 0,
          },

          '& .clearInput': {
            cursor: 'pointer',
            color: tokens.theme.fg.action.linkNeutral,

            '&:hover': {
              color: tokens.theme.fg.action.linkNeutralHover,
            },
          },

          '&.DsInput-disabled': {
            '& .clearInput': {
              color: tokens.theme.disabled.fg,
            },
          },
        },

        '&& .MuiFilledInput-root.MuiInputBase-formControl': {
          padding: '0 8px',
          borderRadius: 0,
          border: 'none',
          color: tokens.theme.fg.structural.primary,
        },
        '&&& .Mui-focused': {
          boxShadow: 'none',
        },

        '&:hover, &:focus-within': {
          borderBottomColor: tokens.theme.border.action.neutralHover,

          '&:has(.DsInput-disabled)': {
            borderBottomColor: tokens.theme.disabled.border,
          },
        },

        '&& .DS-Input-adornment-root': {
          color: tokens.theme.fg.structural.secondary,
        },
        '&:has(.DsInput-disabled)': {
          '&& .DS-Input-adornment-root': {
            color: tokens.theme.disabled.fg,
          },
        },
      },
      large: listSearchInput.setValues({
        adornmentGap: '14px',
      }),
      medium: listSearchInput.setValues({
        adornmentGap: '12px',
      }),
      small: listSearchInput.setValues({
        adornmentGap: '10px',
      }),
    },
  },
};
